import React, { useEffect, useRef, useState } from "react";

import Alert from "../../crmpostventa-shared/components/Alert";
import Modal from "../../crmpostventa-shared/components/Modal";
import { isMine } from "../ipcFormat";
import { createCorrelationId, describeSaveError } from "./gestionarIpcModel";
import { OBSERVACION_MAX } from "../nuevaGestion/nuevaGestionIpcModel";

const TITLE_ID = "crm-ipc-asignar-title";
const USUARIO_ID = "crm-ipc-asignar-usuario";
const OBSERVACION_ID = "crm-ipc-asignar-observacion";

const hasResponsable = (gestion) => gestion?.responsableUsuarioId !== null && gestion?.responsableUsuarioId !== undefined;

/**
 * "Asignar gestión" dialog (design Prompt 6, point 4; D25): any asesor who can manage may assign a gestión to another
 * asesor. The list leaves out the current responsible. When the gestión belongs to another asesor, the reason is
 * required and stays in the timeline. Used by the row menu and by Gestionar.
 *
 * onSubmit({ usuarioId, observacion, correlationId, nombre, reasignada }) does the request and resolves on success
 * (the caller closes the dialog) or rejects: the error shows inside the dialog unless the caller already closed it
 * (a conflict). The correlationId is one per attempt: the same data reuses it.
 */
export default function AsignarGestionDialog({ gestion, responsables = [], currentUserId = null, darkMode = false, onClose, onSubmit }) {
  const [usuarioId, setUsuarioId] = useState("");
  const [observacion, setObservacion] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const attemptRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const assigned = hasResponsable(gestion);
  // Taking it away from another asesor needs the reason (D25).
  const needsReason = assigned && !isMine(gestion, currentUserId);
  const options = responsables.filter((responsable) => !assigned || Number(responsable.usuarioId) !== Number(gestion.responsableUsuarioId));
  const actual = assigned
    ? `${gestion.responsableNombre || "Otro asesor"}${isMine(gestion, currentUserId) ? " (vos)" : ""}`
    : "Sin asignar";

  useEffect(() => {
    if (!focusRequest) return;
    const target = errors.usuario ? document.getElementById(USUARIO_ID) : document.getElementById(OBSERVACION_ID);
    target?.focus();
    // Only a failed confirm moves the focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  const confirm = async () => {
    if (saving) return;
    const next = {};
    if (!usuarioId) next.usuario = "Elegí a quién asignarla";
    const reason = observacion.trim();
    if (needsReason && !reason) next.observacion = "Contá por qué la reasignás";
    else if (reason.length > OBSERVACION_MAX) next.observacion = `Acortá la observación a ${OBSERVACION_MAX} caracteres como máximo`;
    setErrors(next);
    if (Object.keys(next).length) {
      setFocusRequest((count) => count + 1);
      return;
    }

    const body = { usuarioId: Number(usuarioId), observacion: reason || null };
    const key = JSON.stringify(body);
    if (attemptRef.current?.key !== key) attemptRef.current = { key, correlationId: createCorrelationId() };
    const nombre = options.find((option) => String(option.usuarioId) === String(usuarioId))?.nombre || "otro asesor";

    setSaving(true);
    setSubmitError(null);
    try {
      await onSubmit?.({ ...body, correlationId: attemptRef.current.correlationId, nombre, reasignada: assigned });
    } catch (error) {
      if (!mountedRef.current) return;
      setSubmitError(describeSaveError(error));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  return (
    <Modal
      titleId={TITLE_ID}
      title="Asignar gestión"
      size="xs"
      className="crm-ipc"
      darkMode={darkMode}
      closeDisabled={saving}
      onClose={onClose}
      headExtra={<span className="crm-rollo__s">{`${gestion?.nombreComercio || ""} · Responsable actual: ${actual}`}</span>}
      footer={
        <>
          <span className="crm-rollo__grow crm-rollo__help">
            <span className="crm-rollo__req">*</span> Obligatorio
          </span>
          <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="crm-rollo__btn crm-rollo__btn--primary"
            disabled={saving || !options.length}
            aria-busy={saving || undefined}
            onClick={confirm}
          >
            {saving ? (
              <>
                <span className="crm-rollo__spinner" aria-hidden="true" />
                Guardando…
              </>
            ) : (
              "Asignar"
            )}
          </button>
        </>
      }
    >
      {submitError ? (
        <Alert tone="error" role="alert" title={submitError.title}>
          {submitError.text}
        </Alert>
      ) : null}
      <fieldset className="crm-rollo__form crm-rollo__form--single" disabled={saving}>
        <legend className="crm-rollo__sr-only">Asignar gestión</legend>
        <div className={`crm-rollo__fld crm-rollo__fld--full${errors.usuario ? " has-error" : ""}`}>
          <span className="crm-rollo__fld-label" id={`${USUARIO_ID}-label`}>
            Asignar a
            <span className="crm-rollo__req" aria-hidden="true">
              {" *"}
            </span>
          </span>
          {options.length ? (
            <div
              className="crm-rollo__rcards"
              role="radiogroup"
              aria-labelledby={`${USUARIO_ID}-label`}
              aria-describedby={errors.usuario ? `${USUARIO_ID}-error` : undefined}
            >
              {options.map((option, index) => {
                const checked = String(option.usuarioId) === String(usuarioId);
                return (
                  <label key={option.usuarioId} className={`crm-rollo__rcard${checked ? " is-selected" : ""}`}>
                    <input
                      id={index === 0 ? USUARIO_ID : undefined}
                      type="radio"
                      name={USUARIO_ID}
                      value={option.usuarioId}
                      checked={checked}
                      onChange={() => {
                        setUsuarioId(String(option.usuarioId));
                        setErrors(({ usuario, ...rest }) => rest);
                      }}
                    />
                    <span className="crm-ipc__rcard-title">
                      {option.nombre}
                      {Number(option.usuarioId) === Number(currentUserId) ? <span className="crm-rollo__muted"> (vos)</span> : null}
                    </span>
                  </label>
                );
              })}
            </div>
          ) : (
            <p className="crm-rollo__muted">Todavía no hay asesores para asignar.</p>
          )}
          {errors.usuario ? (
            <span className="crm-rollo__err" id={`${USUARIO_ID}-error`}>
              {errors.usuario}
            </span>
          ) : null}
        </div>
        {needsReason ? (
          <div className="crm-rollo__fld--full">
            <Alert tone="info" title={`La gestión es de ${gestion.responsableNombre || "otro asesor"}. Contá por qué la reasignás: queda en la línea de tiempo.`} />
          </div>
        ) : null}
        <div className={`crm-rollo__fld crm-rollo__fld--full${errors.observacion ? " has-error" : ""}`}>
          <label className="crm-rollo__fld-label" htmlFor={OBSERVACION_ID}>
            {needsReason ? "Motivo de la reasignación" : "Observación"}
            {needsReason ? (
              <span className="crm-rollo__req" aria-hidden="true">
                {" *"}
              </span>
            ) : (
              <span className="crm-rollo__opt"> (opcional)</span>
            )}
          </label>
          <textarea
            className="crm-rollo__input crm-rollo__textarea"
            id={OBSERVACION_ID}
            rows={3}
            maxLength={OBSERVACION_MAX}
            placeholder={needsReason ? "Ej.: está de licencia hasta el lunes" : "Ej.: conoce al comercio"}
            value={observacion}
            aria-required={needsReason || undefined}
            aria-invalid={errors.observacion ? true : undefined}
            aria-describedby={errors.observacion ? `${OBSERVACION_ID}-error` : undefined}
            onChange={(event) => {
              setObservacion(event.target.value);
              setErrors(({ observacion: _removed, ...rest }) => rest);
            }}
          />
          {errors.observacion ? (
            <span className="crm-rollo__err" id={`${OBSERVACION_ID}-error`}>
              {errors.observacion}
            </span>
          ) : null}
        </div>
      </fieldset>
    </Modal>
  );
}
